import React, { useState, useMemo, useCallback } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';
import KanbanColumn from './KanbanColumn';
import KanbanDropZone from './KanbanDropZone';
import {
  COLUMNS,
  getColumns,
  organizeVideosByColumn,
  calculateCheckboxesForColumn,
  getStatusIndex,
} from './kanbanUtils';

/**
 * KanbanBoard — Main Kanban container with drag & drop
 *
 * Props:
 *   videos: array         — Same videos array used by the list view
 *   totalCounts: object   — { planejamento, producao, finalizado, postado } from backend (real totals)
 *   onVideoUpdate: fn     — (videoId, updateData) => Promise
 *   onVideoDelete: fn     — (videoId) => Promise
 *   onOpenScript: fn      — (video) => void — open script drawer
 *   onCardClick: fn       — (video) => void — click on card
 *   isLoading: boolean
 *   isMobile: boolean
 *   mobileTab: string     — Active tab on mobile (status id)
 *   onMobileTabChange: fn — (statusId) => void
 */
const KanbanBoard = ({
  videos,
  totalCounts,
  onVideoUpdate,
  onVideoDelete,
  onChecklistChange,
  onOpenScript,
  onCardClick,
  onOptimisticUpdate,
  onOptimisticRollback,
  onConfirmAction,
  isLoading,
  isMobile,
  mobileTab,
  onMobileTabChange,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [draggedVideo, setDraggedVideo] = useState(null);

  // Translated column definitions
  const columns = useMemo(() => getColumns(t), [t]);

  // Organize videos into columns (memo'd for performance)
  const columnData = useMemo(() => organizeVideosByColumn(videos), [videos]);

  // Contagem local dos vídeos em memória
  const columnCounts = useMemo(
    () => ({
      planejamento: columnData.planejamento.length,
      producao: columnData.producao.length,
      finalizado: columnData.finalizado.length,
      postado: columnData.postado.length,
    }),
    [columnData]
  );

  // Badge: usar totais do backend quando disponíveis (reflete total real, não só carregados)
  const displayCounts = useMemo(
    () => ({
      planejamento: totalCounts?.planejamento ?? columnCounts.planejamento,
      producao: totalCounts?.producao ?? columnCounts.producao,
      finalizado: totalCounts?.finalizado ?? columnCounts.finalizado,
      postado: totalCounts?.postado ?? columnCounts.postado,
    }),
    [totalCounts, columnCounts]
  );

  // ── Drag handlers ──

  const handleDragStart = useCallback(
    (result) => {
      setIsDragging(true);
      const video = videos.find(
        (v) => v.id.toString() === result.draggableId
      );
      setDraggedVideo(video || null);
    },
    [videos]
  );

  // Helper: execute the actual move (optimistic + API)
  const executeMove = useCallback(
    (video, targetStatus, newCheckboxValues) => {
      const previousVideos = videos;

      if (onOptimisticUpdate) {
        onOptimisticUpdate(video.id, { ...newCheckboxValues, status: targetStatus });
      }

      onVideoUpdate(video.id, newCheckboxValues)
        .then(() => {
          const targetCol = columns.find((c) => c.id === targetStatus);
          toast.success(t('kanban.video_moved_to', { column: targetCol?.title || targetStatus }));
        })
        .catch(() => {
          if (onOptimisticRollback) {
            onOptimisticRollback(previousVideos);
          }
          toast.error(t('kanban.error_move_video'));
        });
    },
    [videos, columns, onVideoUpdate, onOptimisticUpdate, onOptimisticRollback, t]
  );

  const handleDragEnd = useCallback(
    (result) => {
      setIsDragging(false);
      setDraggedVideo(null);

      const { destination, source, draggableId } = result;

      if (!destination) return;

      // ── Action drop zones ──
      if (destination.droppableId === 'ACTION_DELETE') {
        const video = videos.find(
          (v) => v.id.toString() === draggableId
        );
        if (video && !video.is_imported && onVideoDelete) {
          if (onConfirmAction) {
            onConfirmAction({
              icon: <Trash2 />,
              title: t('kanban.delete_video'),
              message: t('kanban.confirm_delete_message'),
              confirmLabel: t('kanban.delete'),
              danger: true,
              onConfirm: async () => {
                try {
                  await onVideoDelete(video.id);
                  toast.success(t('kanban.video_deleted'));
                } catch {
                  toast.error(t('kanban.error_delete_video'));
                }
              },
            });
          }
        }
        return;
      }

      if (destination.droppableId === 'ACTION_GENERATE_AUDIO') {
        const video = videos.find(
          (v) => v.id.toString() === draggableId
        );
        if (video && !video.is_imported) {
          window.location.href = `/audio-generator?videoId=${video.id}&canalId=${video.canal_id}`;
        }
        return;
      }

      // Same position — no-op
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }

      const video = videos.find(
        (v) => v.id.toString() === draggableId
      );
      if (!video) return;

      // Imported videos can't be moved
      if (video.is_imported) {
        toast.error(t('kanban.imported_cannot_move'));
        return;
      }

      const targetStatus = destination.droppableId;
      const sourceStatus = source.droppableId;

      // Block drag to postado
      if (targetStatus === 'postado') {
        toast.error(t('kanban.use_checkbox_to_publish'));
        return;
      }

      // Calculate new checkbox values
      const newCheckboxValues = calculateCheckboxesForColumn(
        targetStatus,
        video
      );
      if (!newCheckboxValues) return;

      // Backward move — show confirm dialog, but DON'T block UI
      const sourceIdx = getStatusIndex(sourceStatus);
      const targetIdx = getStatusIndex(targetStatus);

      if (targetIdx < sourceIdx) {
        if (onConfirmAction) {
          onConfirmAction({
            icon: <AlertTriangle />,
            title: t('kanban.backward_move_title'),
            message: t('kanban.backward_move_message'),
            confirmLabel: t('kanban.yes_continue'),
            danger: true,
            onConfirm: () => {
              executeMove(video, targetStatus, newCheckboxValues);
            },
          });
        }
        return;
      }

      // Forward move — execute immediately (no confirmation needed)
      executeMove(video, targetStatus, newCheckboxValues);
    },
    [videos, columns, onVideoDelete, onConfirmAction, executeMove, t]
  );

  // ── Mobile: tab-based rendering ──
  if (isMobile) {
    const activeStatus = mobileTab || 'planejamento';
    const activeColumn = columns.find((c) => c.id === activeStatus) || columns[0];

    return (
      <div style={{ width: '100%' }}>
        {/* Mobile tab bar */}
        <div
          style={{
            display: 'flex',
            gap: '4px',
            padding: '4px',
            background: 'var(--dp-bg-card, rgba(30,30,30,0.5))',
            borderRadius: '12px',
            marginBottom: '16px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {columns.map((col) => {
            const isActive = col.id === activeStatus;
            return (
              <button
                key={col.id}
                onClick={() => onMobileTabChange(col.id)}
                style={{
                  flex: '1 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '8px 12px',
                  borderRadius: '9px',
                  border: 'none',
                  fontSize: '11px',
                  fontWeight: isActive ? 600 : 500,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: isActive ? col.color : 'transparent',
                  color: isActive
                    ? '#fff'
                    : 'var(--dp-text-secondary, #A0A0A0)',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{col.icon}</span>
                <span>{displayCounts[col.id]}</span>
              </button>
            );
          })}
        </div>

        {/* Active column content */}
        <DragDropContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <KanbanColumn
            column={activeColumn}
            videos={columnData[activeStatus] || []}
            count={displayCounts[activeStatus] || 0}
            isLoading={isLoading}
            onOpenScript={onOpenScript}
            onCardClick={onCardClick}
            isMobile
          />
          <KanbanDropZone draggedVideo={draggedVideo} isDragging={isDragging} />
        </DragDropContext>
      </div>
    );
  }

  // ── Desktop: full board ──
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '500px',
        overflow: 'hidden',
      }}
    >
      <DragDropContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Columns container — altura CONSTANTE durante o drag.
            NUNCA mudar dimensões aqui com isDragging: o @hello-pangea/dnd captura
            a geometria das colunas no início do arrasto; um reflow no meio quebra
            o hit-testing e causa travamento (era a causa do lag entre colunas).
            O espaço das drop zones (80px) fica PRÉ-RESERVADO abaixo, sempre. */}
        <div
          style={{
            display: 'flex',
            gap: '16px',
            height: 'calc(100vh - 310px)',
            minHeight: '460px',
            overflowX: 'hidden',
            overflowY: 'hidden',
            width: '100%',
          }}
        >
          {columns.map((column) => (
            <KanbanColumn
              key={column.id}
              column={column}
              videos={columnData[column.id]}
              count={displayCounts[column.id]}
              isLoading={isLoading}
              onChecklistChange={onChecklistChange}
              onOpenScript={onOpenScript}
              onCardClick={onCardClick}
            />
          ))}
        </div>

        {/* Drop zones — below columns, slide open during drag */}
        <KanbanDropZone draggedVideo={draggedVideo} isDragging={isDragging} />
      </DragDropContext>

      {/* Global keyframes for this board */}
      <style>{`
        @keyframes kanban-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default KanbanBoard;
